import React from 'react';
import { 
  hasAnyUnansweredComments,
  sortCommentsByUrgency,
  getCommentDisplayName,
  needsConsultantReply,
  isMarkedAsOK,
  isOKStale
} from './feedbackLogic';
import CommentCard from './CommentCard';
import FeedbackStatusBadge from './FeedbackStatusBadge';

/**
 * FeedbackSection - Generisk container för feedback-utbyte (PER-RAD KOMMENTARER)
 * Kan användas både för byggkonsult (visar kundens kommentarer) och kund (visar byggkonsultens svar)
 * Denna komponent är designad för "per-rad" feedback som i Estimat-flödet
 */
const FeedbackSection = ({
  incomingComments,      // Den ANDRA personens kommentarer (som jag läser)
  myResponses,           // Mina svar på deras kommentarer
  formData,
  expandedFeedbackItems,
  setExpandedFeedbackItems,
  newResponses,          // Nya osparade svar
  savedResponses,        // Sparade men ej skickade svar
  onResponseChange,
  onResponseSave,
  onResponseDelete,
  onResponseEdit,
  onEntryEdit,           // (commentKey, entryIndex, newText) → redigera tidigare thread-entry
  onMarkAsOK,
  onUndoMarkOK,
  onSendAllResponses,
  hasUnsavedChangesInResponse,
  viewMode = 'consultant', // 'consultant' eller 'customer'
  onProofread
}) => {
  // Om ingen feedback finns, visa ingenting
  if (!incomingComments || Object.keys(incomingComments).length === 0) {
    return null;
  }

  // Samla alla kommentarsnycklar.
  // Prioriterade frågor från första-estimatmailet (__budget__, __prioritering__)
  // visas först, sedan övergripande, sedan radkommentarer. __meetingRequest__
  // filtreras bort eftersom det visas i mötessektionen, inte feedback.
  const RESERVED_TOP_KEYS = ['__budget__', '__prioritering__', '__overall__'];
  const keys = [
    ...(incomingComments?.__budget__ ? ['__budget__'] : []),
    ...(incomingComments?.__prioritering__ ? ['__prioritering__'] : []),
    ...(incomingComments?.__overall__ ? ['__overall__'] : []),
    ...Object.keys(incomingComments || {})
      .filter((k) => !RESERVED_TOP_KEYS.includes(k) && k !== '__meetingRequest__')
  ];

  // Sortera med helper-funktion
  const sortedKeys = sortCommentsByUrgency(keys, incomingComments, myResponses);

  // Bestäm om sektionen ska vara öppen (om det finns obesvarade kommentarer)
  const isOpen = hasAnyUnansweredComments(incomingComments, myResponses);

  // Bestäm badge-stil
  const anyNeedsReply = hasAnyUnansweredComments(incomingComments, myResponses);

  if (sortedKeys.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-4 mb-6">
      <details 
        className="bg-white rounded-lg shadow-sm border border-gray-200" 
        open={isOpen}
      >
        <summary className="px-3 sm:px-4 py-2.5 sm:py-3 bg-leadme-pink-dark text-white rounded-t-lg cursor-pointer flex items-center justify-between">
          <h2 className="text-sm sm:text-base font-medium">Feedback</h2>
          <div className="flex items-center gap-3">
            <FeedbackStatusBadge anyNeedsReply={anyNeedsReply} />
            <svg className="w-4 h-4 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </summary>

        <div className="p-2 sm:p-4">
          <div>{/* Övergripande feedback renderas nedan i listan "Kommentarer per rad" */}</div>
          
          <div className="mt-5">
            <div className="space-y-3">
              {sortedKeys.map((commentKey, idx) => {
                const val = incomingComments[commentKey];
                const displayName = getCommentDisplayName(commentKey, formData);
                const myResponseObj = myResponses?.[commentKey];
                
                // Kolla om någon (jag ELLER de) har markerat som OK — men
                // bara om markeringen fortfarande gäller (inte stale pga nytt svar)
                const iMarkedAsOK = isMarkedAsOK(commentKey, myResponses);
                const theyMarkedAsOK = isMarkedAsOK(commentKey, incomingComments);
                const anyHasOK = iMarkedAsOK || theyMarkedAsOK;
                const anyoneMarkedAsOK = anyHasOK && !isOKStale(commentKey, incomingComments, myResponses);

                // Jag behöver bara svara om ingen har markerat som OK (aktuellt) OCH det finns en oläst kommentar
                const needsReply = !anyoneMarkedAsOK && needsConsultantReply(commentKey, incomingComments, myResponses);

                return (
                  <React.Fragment key={commentKey}>
                    {commentKey !== '__overall__' && keys.slice(0, idx).every(k => k === '__overall__') && (
                      <h3 className="text-sm font-semibold text-gray-900 mt-2">Kommentarer per rad</h3>
                    )}
                    
                    <CommentCard
                      commentKey={commentKey}
                      displayName={displayName}
                      customerComment={val}
                      consultantResponse={myResponseObj}
                      customerComments={incomingComments}
                      byggkonsultResponses={myResponses}
                      consultantNeedsToReply={needsReply}
                      isExpanded={expandedFeedbackItems.hasOwnProperty(commentKey) 
                        ? !!expandedFeedbackItems[commentKey] 
                        : needsReply}
                      onToggle={() => setExpandedFeedbackItems(prev => ({ 
                        ...prev, 
                        [commentKey]: !prev[commentKey] 
                      }))}
                      newResponse={newResponses[commentKey] || ''}
                      onResponseChange={(value) => onResponseChange(commentKey, value)}
                      onResponseSave={() => onResponseSave(commentKey)}
                      onResponseDelete={onResponseDelete ? () => onResponseDelete(commentKey) : undefined}
                      onResponseEdit={onResponseEdit ? () => onResponseEdit(commentKey) : undefined}
                      onEntryEdit={onEntryEdit}
                      onMarkAsOK={() => onMarkAsOK(commentKey)}
                      onUndoMarkOK={onUndoMarkOK ? () => onUndoMarkOK(commentKey) : undefined}
                      hasUnsavedChanges={hasUnsavedChangesInResponse(commentKey)}
                      viewMode={viewMode}
                    />
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>

        {/* Skicka alla sparade svar */}
        {Object.keys(savedResponses).length > 0 && (
          <div className="px-2 sm:px-4 pb-3 sm:pb-4">
            <div className="flex justify-end gap-2">
              {onProofread && (
                <button
                  type="button"
                  onClick={onProofread}
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-md flex items-center gap-2"
                  title="Korrekturläs dina svar innan de skickas"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                  Korrekturläs
                </button>
              )}
              <button
                type="button"
                onClick={onSendAllResponses}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
                Skicka alla ({Object.keys(savedResponses).length})
              </button>
            </div>
          </div>
        )}
      </details>
    </div>
  );
};

export default FeedbackSection;

